<template>
  <DrawerPortal.Content :variant="variant ?? 'default'">
    <div
      ref="container"
      :key="id"
      role="region"
      :aria-labelledby="titleId"
      tabindex="-1"
      class="flex min-h-0 grow flex-col bg-white text-sm text-black outline-none transition @container/drawer dark:bg-sidebar dark:text-white"
      :data-drawer-variant="variant"
    >
      <Header
        :id="titleId"
        :title="title"
        :pending="pending"
        :icon="icon"
        :image="image"
        :is-scrolled="isScrolled"
      >
        <template #title>
          <slot name="title" />
        </template>

        <template #actions>
          <slot name="actions" />

          <HeaderAction label="Close" icon="x-mark" @click="emit('close')" />
        </template>
      </Header>

      <Callout.Container v-if="slots.callouts">
        <slot name="callouts"></slot>
      </Callout.Container>

      <div
        class="relative flex min-h-0 grow flex-col overflow-y-auto"
        @scroll="handleScroll"
      >
        <slot></slot>
      </div>

      <div v-if="slots.footer" class="shrink-0 border-t border-border">
        <slot name="footer"></slot>
      </div>
    </div>
  </DrawerPortal.Content>
</template>

<script lang="ts" setup>
import { type Component, onMounted, ref, useId, useTemplateRef } from "vue";

import Callout from "@/design_system/Callout";
import { useKeybinds } from "@/lib/hotkeys";

import HeaderAction from "./HeaderAction.vue";
import Header from "./internal/Header.vue";
import * as DrawerPortal from "./Portal";

const slots = defineSlots<{
  title(): unknown;
  actions(): unknown;
  callouts(): unknown;
  footer(): unknown;
  default(): unknown;
}>();

defineProps<{
  title: string;
  pending?: boolean;
  image?: string;
  icon?: Component;
  variant?: DrawerPortal.DrawerVariant;
}>();

const emit = defineEmits<{
  close: [];
}>();

const id = useId();
const titleId = useId();

const container = useTemplateRef("container");
const isScrolled = ref(false);

onMounted(() => {
  container.value?.focus({ preventScroll: true });
});

const handleScroll = (event: Event) => {
  const target = event.target as HTMLElement;
  isScrolled.value = target.scrollTop > 0;
};

useKeybinds({
  Escape: {
    name: `Close drawer`,
    group: `Drawer`,
    run(ev) {
      if (ev.defaultPrevented) {
        return;
      }

      emit("close");
      return true;
    },
  },
});
</script>
